import { html } from '../../mixins/katapult-element';
import { html as litHtml } from 'lit';
import { SharingLink } from './sharing-link.js';
import { DialogService } from '../../modules/DialogService.js';
import { ModelOptions } from '../../modules/ModelOptions';
import '../katapult-elements/katapult-button.js';

export class ModelSharingLink extends SharingLink {
  static async updateSharedModel(options) {
    const res = await fetch('https://us-central1-katapult-pro-shared-resources.cloudfunctions.net/shareModelSpace', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        delete: options.delete,
        linkId: options.linkId,
        companyId: options.companyId,
        serverId: window.config.appName,
        jwt: await firebase.auth().currentUser?.getIdToken(true)
      })
    });
    const data = await res.json().catch(() => {});
    if (data?.err || !res.ok) throw new Error(data?.err);
    return data;
  }
  static get template() {
    return html`
      ${super.template}
      <template is="dom-if" if="[[!sharingLink.pending]]">
        <template is="dom-if" if="[[isRecipient]]">
          <div>
            <material-icon icon="business"></material-icon>
            <span class="label">Owner:</span>
            <span>[[sharingLink.metadata.source_company_name]]</span>
          </div>
        </template>
        <div>
          <material-icon icon="update"></material-icon>
          <span class="label">Last Updated: </span>
          <span>[[formatDate(sharingLink.metadata.updated_at)]]</span>
          <template is="dom-if" if="[[isSender]]">
            <div style="flex-grow: 1;"></div>
            <katapult-button
              icon="sync"
              size="18"
              color="var(--secondary-color)"
              success="[[sharingLink.metadata.up_to_date]]"
              error="[[!sharingLink.metadata.up_to_date]]"
              disabled-on-success
              disabled-on-error
              >[[ifElse(sharingLink.metadata.up_to_date, 'Up to Date', 'Out of Date')]]</katapult-button
            >
          </template>
        </div>
      </template>
      <template is="dom-if" if="[[isSender]]">
        <div>
          <span class="label">Automatically publish when saved</span>
          <sl-switch checked="[[sharingLink.auto_publish]]" on-sl-change="_handleAutoPublishChange"> </sl-switch>
        </div>
      </template>
      <template is="dom-if" if="[[isRecipient]]">
        <div>
          <span class="label">Automatically sync when model changes</span>
          <sl-switch checked="[[sharingLink.auto_sync]]" on-sl-change="_handleAutoSyncChange"> </sl-switch>
        </div>
      </template>
      <template is="dom-if" if="[[_outOfDate]]">
        <div style="padding: 0 12px;">
          <div
            style="display: flex; align-items: center; justify-content: center; padding: 16px; gap: 16px; color: var(--paper-red-500); border: 1px solid var(--paper-grey-200); background: var(--paper-grey-50); border-radius: 16px;"
          >
            <material-icon icon="warning"></material-icon>
            <div>This model needs to be updated</div>
            <katapult-button red icon="sync" callback="_updateModels">Update Now</katapult-button>
          </div>
        </div>
      </template>
    `;
  }

  static get observers() {
    return [...super.observers, '_updateOptions(isRecipient)', '_updateOutOfDate(sharingLink.*)'];
  }

  async _delete() {
    if (!this.sharingLink.pending) await this._updateSharedModel({ delete: true });
    await super._delete();
  }

  _deleteWarningTemplate() {
    const recipientDescriptionTemplate = this._companyDescriptionTemplate(this.sharingLink.recipient);
    const modelDescriptionTemplate = this._companyDescriptionTemplate({
      company: `${this.sharingLink.sender.company} model`,
      server: this.sharingLink.sender.server
    });
    return litHtml`<strong>${recipientDescriptionTemplate}</strong> will no longer have access to the <strong>${modelDescriptionTemplate}</strong>`;
  }

  _getCompanyIcon() {
    return this.companyId == this.sharingLink.sender?.company_id ? super._getCompanyIcon() : 'public';
  }

  _getCompanyLabel() {
    return this.companyId == this.sharingLink.sender?.company_id ? super._getCompanyLabel() : 'Model';
  }

  _updateOptions() {
    super._updateOptions();
    const aliasOption = { label: 'Set Alias', icon: 'create', callback: 'renameModel' };
    if (this.isRecipient) this._options = [aliasOption, ...this._options];
  }

  renameModel() {
    {
      // Create the dialog.
      const { dialog, promise } = DialogService.dialog({
        properties: { title: `Rename Model`, icon: 'create' },
        bodyStyle: '',
        template: litHtml`
          <p slot="body">Please enter an alias to use below:</p>
          <paper-input slot="body" label="Model"></paper-input>
          <katapult-button slot="buttons" outline dialog-dismiss>Cancel</katapult-button>
          <div slot="buttons" style="flex-grow: 1;"></div>
          <katapult-button slot="buttons" id="confirm" color="var(--secondary-color)" show-error>Save</katapult-button>
        `
      });
      // Get the confirm button.
      const button = dialog.querySelector('katapult-button[id="confirm"]');
      // Get the input and setup a value listener on it to enable the confirm button when valid.
      const input = dialog.querySelector('paper-input');
      // Set currrent description on input.
      input.value = this.modelLabel ?? this.sharingLink.sender.company ?? '';
      // Click button when enter pressed.
      input.addEventListener('keypress', (e) => e.key == 'Enter' && button.click());
      // Setup callback on the button to set the description.
      button.callback = async () => {
        const name = input.value.trim();
        await ModelOptions.update({ key: this.modelId, label: name }, { companyId: this.companyId });
        dialog.close();
      };
      // Focus the input.
      setTimeout(() => input.focus());
    }
  }
  _updateOutOfDate() {
    this._outOfDate = !this.sharingLink.pending && !this.sharingLink.metadata?.up_to_date;
  }

  async _updateSharedModel(options) {
    return await ModelSharingLink.updateSharedModel({ linkId: this.linkId, companyId: this.companyId, ...options });
  }

  async _handleAutoPublishChange(e) {
    const autoPublish = e.target.checked;
    this.sharingLink.auto_publish = autoPublish;
    await this.updateMetadata({ auto_publish: autoPublish });
  }

  async _handleAutoSyncChange(e) {
    const autoSync = e.target.checked;
    this.sharingLink.auto_sync = autoSync;
    await this.updateMetadata({ auto_sync: autoSync });
  }

  async updateMetadata(metadata) {
    if (!metadata || Object.keys(metadata).length === 0) return;
    metadata.updated_at = Date.now();
    const updated = await SharingLink.serverAction({
      action: 'update_metadata',
      linkId: this.linkId,
      companyId: this.companyId,
      metadata
    });
    this.sharingLink = updated.sharingLink;
  }

  async _updateModels(e) {
    const button = e.currentTarget;
    const companyId = this.isSender ? this.sharingLink.sender.company_id : this.sharingLink.recipient.company_id;
    if (!this.sharingLink.pending) {
      const res = await ModelSharingLink.updateSharedModel({ linkId: this.linkId, companyId: companyId });
      if (res?.err) {
        button.showError(res.err);
        return;
      } else {
        this.sharingLink = res.sharingLink;
      }
    }
    button.showSuccess();
  }
}

window.customElements.define('model-sharing-link', ModelSharingLink);
